define([
	"dojo/_base/array",
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dijit/DialogUnderlay",
	"csi",
	"csi/action",
	"csi/action/ActionView",
	"csi/action/Controller",
	"csi/def",
	"csi/locale",
	"csi/reflection",
	"csi/xml"
], function(array, declare, lang, DialogUnderlay, csi, action, ActionView, Controller, def, locale, reflection, xml) {

	return declare(Controller, {
		// Boolean: indicates node(s) will be inserted after m_urlContext
		bInsertAfter: false,

		// Boolean: indicates if use the node when creating form
		bUseNodeForForm: true,

		// String: qualifier attribute if operating on map
		idQualifier: null,

		// String: qualifier for new node - changes as action is applied for multiple sources
		qualifier: null,

		// String: next sibling if inserting into ordered list
		m_urlContext: null,

		// Attribute: relationship being acted upon
		m_relation: null,

		// Node[]: array of selected source nodes
		m_aSources: null,

		// Node: current source node for action - changes as action is applied for multiple sources
		m_nodeSource: null,

		// xml.ChangeNode: ChangeNode operation for each node (if any)
		m_cnNew: null,

		// Array|Object: container for new nodes from action
		m_value: null,

		// Number: length of selected source nodes
		_sourceSelectedLength: 0,

		// Node[]: array of nodes still to check for uniqueness
		_sourcesToCheck: null,


		constructor: function(/*Object*/params) {
			this.m_relation = reflection.getRelation(this.getParent(), this.m_idAttrParent);
			this.m_aSources = [];

			// reset node url since action applies to parent node
			this.m_urlNode = null;
		},

		/**
		 * @override
		 * If more to do, then disable dialog underlay so screen will not flash when closing/opening dialogs
		 */
		closeDialog: function() {
			if (this._popupDialog) {
				DialogUnderlay.setDisabled(this._sourcesToCheck && this._sourcesToCheck.length);
			}

			this.inherited(arguments);
		},

		getFormData: function() {
			// summary: assuming that all move cases are optional and does not require selection
			if (this.m_formData) return this.m_formData;

			var data = this.inherited(arguments);

			if (!this._sourcesToCheck) {
				data.m_bRequireOne = false;
				data.m_bOmitSelections = true;
			}

			return data;
		},

		getActionNodeType: function() {
			// summary: return the type of the node the controller is acting upon
			var node = this.m_nodeSource;
			return node ? node.$Type : null;
		},

		getMovedNodes: function() {
			// summary: get nodes resulting from action for postprocessing
			var a = this._getMoveFromUrls();
			return a ? array.map(a, function(url) {return this.m_cache.getNode(url);}, this) : [];
		},

		getReturnNodeRequest: function(/*String[]*/urlsNew) {
			// summary: add new urls to request
			return this.inherited(arguments, [urlsNew.concat(this._getMoveFromUrls())]);
		},

		/**
		 * @override
		 * Return true if still more sources to check
		 */
		hasMoreToProcess() {
			return !!this._sourcesToCheck?.length;
		},

		storeSources: function() {
			// summary: store selected nodes from form if there was one - for Copy the source is set immediately without selecting more nodes
			var fd = this.m_formData;
			if (fd) {
				var nodes = fd.getSelectedNodes();
				if (nodes.length) {
					this._sourceSelectedLength = nodes.length;
					this.m_aSources = [];
					fd.resetSelections();

					array.forEach(nodes, function(node) {
						this.m_aSources.push(this.m_cache.cacheNode(node));
					}, this);
				}
			}
		},

		/**
		 * Return true if there is a form to open for each selection
		 * @return {boolean}
		 */
		hasFormForEachSelection() {
			if (this.m_action.idForm) {
				return !!this._getFormView(this.m_aSources.first(), true);
			}

			return false;
		},

		process: function() {
			// summary: if first time process called, then store selected nodes and determine if they
			// can be published all at once or one-by-one.
			if (!this._sourcesToCheck) {
				this.storeSources();

				// if there is a form to show for each selection, then open one by one
				if (this.m_aSources?.length && this.hasFormForEachSelection()) {
					this._sourcesToCheck = this.m_aSources;
					this.postProcess(null, true);
					return;
				}
			}

			// issue operations to copy source(s)
			if (this.m_aSources && this.m_aSources.length) {
				this.m_value = this.idQualifier ? {} : [];

				// apply action to each source
				array.forEach(this.m_aSources, this._processSource, this);

				// add nodes to parent if not using custom request (which should handle adding to parent on their own)
				if (!this.m_action.m_request) {
					this._toWriterParent();
				}

				this._toWriterValidateNode();
			}

			// continue with publishing
			this.publish();
		},

		_processSource: function(/*Node*/node) {
			// summary: write node operations for source to copy
			this.m_urlNode = this.createUrlFrom(node);
			this.m_nodeSource = node;
			this._toWriterSource();
			this.toWriterFields(this.m_urlNode);
			this._onAction();

			// track reference to new node for parent
			if (this.m_value) {
				if (this.idQualifier) {
					// get qualifier from form if at most one source - otherwise get from source node
					var key = this.qualifier || this.m_formData.getFieldValue(this.idQualifier) || this.m_nodeSource[this.idQualifier] || "";
					this.m_value[key] = this.m_urlNode;
				} else {
					this.m_value.push(this.m_urlNode);
				}
			}
		},

		createUrlFrom: function(nodeSource) {
			// summary: move action does not create new node, so just return url
			return xml.nodeGetURL(nodeSource);
		},

		toWriter: function() {
			// summary: generate custom call method for action if so configured
			var call = this.inherited(arguments);
			if (call) {
				// add request parameters specific to NewFrom/MoveFrom
				call.addParameter(xml.PROP_URL_FROM, xml.ATTR_REF, xml.nodeGetURL(this.m_nodeSource));
			}

			return call;
		},

		_toWriterParent: function() {
			// summary: issue operations to add new nodes to parent
			var obj;

			if (!this.m_relation) return;

			// if inserting into ordered list, then create hashtable from array
			if (this.m_urlContext && this.m_relation.isOrdered()) {
				var value = this.getParent()[this.m_idAttrParent];
				if (value && value.length) {
					var n = array.indexOf(value, this.m_urlContext);
					if (n >= 0) {
						if (this.bInsertAfter) {
							++n;
						}

						obj = {};
						array.forEach(this.m_value, function(url) {obj[n++] = url;});
						this.m_writer.changeNode(this.m_urlParent).changeAttributeKeys(this.m_idAttrParent, this.m_relation.getType(), obj, true);
					}
				}
			}

			// otherwise just append to current value
			if (!obj) {
				this.m_writer.changeParent(this.m_urlParent, this.m_idAttrParent, this.m_relation.getType(), this.m_value, true);
			}
		},

		_toWriterSource: function() {
			// summary: output operations necessary to move source node to new parent
			var call = this.toWriter();
			if (!call) {
				// if no special request, then just remove node from its current parent (if any)
				var urlParentOld = xml.nodeGetAttributeValue(this.m_nodeSource, xml.ATTR_PARENT);

				if (xml.isValidURL(urlParentOld)) {
					this.m_writer.changeParent(urlParentOld, this.m_idAttrParent, this.m_relation.getType(), [this.m_urlNode], false);
				}
			}

			return call;
		},

		_toWriterValidateNode: function() {
			// summary: validate moved nodes
			array.forEach(this._getMoveFromUrls(), function(url) {
				this.validateNode(url, false);
			}, this);
		},

		_getFormView: function(/*Node*/node, /*Boolean*/bCheckOnly) {
			const defView = def.getNodeViewDef(node, this.m_action.idForm, false, bCheckOnly);
			return this.isValidActionView(defView);
		},

		_getMoveFromUrls: function() {
			// summary: return array of urls affected by action
			return this.idQualifier ? csi.objectValues(this.m_value) : this.m_value;
		},

		/**
		 * @override
		 * Process next source only after dialog has been closed for previous one
		 */
		postProcess(bStatus, bRepeat) {
			bRepeat = (bStatus !== false) && !!this._sourcesToCheck?.length;

			this.inherited(arguments, [bStatus, bRepeat]);

			if (bRepeat) {
				this.when(this._popupDialog?._fadeOutDeferred, () => {
					this.resumeProcess();
					this._popupSourceForm(this._sourcesToCheck.shift());
				});
			}
		},

		_popupSourceForm: function(/*Node*/node) {
			// summary: popup form for next source
			var formView = this._getFormView(node, false);
			if (formView) {
				this.m_aSources = [node];
				this.m_urlNode = xml.nodeGetURL(node);
				this.m_formData = null;
				this.m_value = null;
				this.openDialog(new ActionView(this, formView, this.bUseNodeForForm ? node : null));
			} else {
				// if no form found, then show error
				console.warn("_popupSourceForm - form not found " + this.m_action.idForm);
				this._sourcesToCheck = null;
				this.postProcess(false);
			}
		},

		_setRefNode: function(/*DOMNode*/refNode) {
			// summary: set refNode before which the new item will be inserted
			if (refNode) {
				this.m_urlContext = this.widget.getRefNodeUrl(refNode);
			}

			this.inherited(arguments);
		}
	});
});